/**
 * The kitchen screen's orders sync loop (feature 334, research D4, D5, D11).
 *
 * One full read (limit 500) fills the React Query cache and gives a cursor; from
 * then on a cheap `changes` call every 2 s patches the cache in place. A full
 * read happens again on `resync`, on a socket (re)connect and every 5 minutes.
 *
 * Compatibility path: when the feed answers 404 (switch off, old backend) or
 * fails with 5xx three times in a row, the loop goes back to what the screen did
 * before — the full list every 15 s with a socket, 5 s without — and tries the
 * feed again after 5 minutes.
 *
 * No React in here: the hook only acquires the loop, so it runs under plain
 * Jest. Time and requests are injected.
 */
import { applyChanges } from "./apply-changes";
import type { ChangesResponse } from "./apply-changes";
import type { Freshness } from "./freshness";
import { createScheduler, type Scheduler } from "./scheduler";
import { meterScheduler } from "./metered-scheduler";
import type { Metrics } from "./metrics";
import { FEED_TIMEOUT_MS, FULL_LIST_TIMEOUT_MS, httpStatusOf } from "./request-signal";

export const FEED_INTERVAL_MS = 2_000;
export const FULL_RELOAD_MS = 5 * 60_000;
export const COMPAT_RETRY_MS = 5 * 60_000;
export const COMPAT_POLL_WITH_SOCKET_MS = 15_000;
export const COMPAT_POLL_WITHOUT_SOCKET_MS = 5_000;
const SERVER_ERRORS_BEFORE_COMPAT = 3;
/** A reload request this soon after a full read adds nothing (start-up: the socket opens as the first read lands). */
const RELOAD_DEBOUNCE_MS = 3_000;

const FEED = "orders_feed";
const FULL = "orders_full";

export interface OrdersSyncApi {
  listWithCursor(
    clientId: string,
    params: undefined,
    signal: AbortSignal,
  ): Promise<{ orders: any[]; cursor: string | undefined }>;
  changes(clientId: string, since: string, signal: AbortSignal): Promise<ChangesResponse<any>>;
}

export interface CacheLike {
  getQueryData<T>(key: readonly unknown[]): T | undefined;
  setQueryData(key: readonly unknown[], data: any): unknown;
}

export interface OrdersSyncOptions {
  clientId: string;
  queryClient: CacheLike;
  api: OrdersSyncApi;
  freshness: Freshness;
  isSocketUp: () => boolean;
  scheduler?: Scheduler;
  /** Records the waiting time of the list and feed calls (feature 334, US3). */
  metrics?: Metrics;
  /** Called after a feed answer changed the list (the station view reads it). */
  onChanges?: () => void;
  setTimeout?: (fn: () => void, ms: number) => unknown;
  clearTimeout?: (id: unknown) => void;
  now?: () => number;
}

export type SyncMode = "feed" | "compat";

export interface OrdersSync {
  start(): void;
  stop(): void;
  /** One full read, shared by every caller while it is in flight. Rejects on failure. */
  fetchFull(): Promise<any[]>;
  triggerPoll(): void;
  requestFullReload(): void;
  mode(): SyncMode;
}

export const ordersCacheKey = (clientId: string) => ["kitchen-orders", clientId, undefined] as const;

export function createOrdersSync(options: OrdersSyncOptions): OrdersSync {
  const { clientId, queryClient, api, freshness } = options;
  const scheduler = meterScheduler(options.scheduler ?? createScheduler(), options.metrics);
  const setT = options.setTimeout ?? ((fn, ms) => setTimeout(fn, ms));
  const clearT = options.clearTimeout ?? ((id) => clearTimeout(id as ReturnType<typeof setTimeout>));
  const now = options.now ?? Date.now;
  const key = ordersCacheKey(clientId);

  let mode: SyncMode = "feed";
  let compatSince = 0;
  let cursor: string | null = null;
  let lastFullAt = 0;
  let serverErrors = 0;
  let stopped = false;
  let started = false;
  let timer: unknown;
  let running = false;
  let pending = false;
  let forceFull = false;
  let fullInFlight: Promise<any[]> | undefined;

  const enterCompat = () => {
    mode = "compat";
    compatSince = now();
    serverErrors = 0;
  };

  function fetchFull(): Promise<any[]> {
    if (fullInFlight) return fullInFlight;
    const run = (async () => {
      const result = await scheduler.run(
        FULL,
        (signal) => api.listWithCursor(clientId, undefined, signal),
        { timeoutMs: FULL_LIST_TIMEOUT_MS },
      );
      if (!result.ok) {
        throw result.reason === "error" ? result.error : new Error(`Order list ${result.reason}`);
      }
      const { orders, cursor: next } = result.value;
      queryClient.setQueryData(key, orders);
      lastFullAt = now();
      serverErrors = 0;
      freshness.markSuccess();
      if (next === undefined || next === null) {
        // An older backend: no cursor, so no feed.
        cursor = null;
        if (mode === "feed") enterCompat();
      } else {
        cursor = next;
      }
      return orders;
    })();
    fullInFlight = run.finally(() => {
      fullInFlight = undefined;
    });
    fullInFlight.catch(() => undefined);
    return fullInFlight;
  }

  const fetchFullQuietly = () => fetchFull().then(
    () => undefined,
    () => undefined,
  );

  async function poll(): Promise<void> {
    const current = queryClient.getQueryData<any[]>(key);
    if (cursor === null || current === undefined) {
      await fetchFullQuietly();
      return;
    }
    const since = cursor;
    const result = await scheduler.run(FEED, (signal) => api.changes(clientId, since, signal), {
      timeoutMs: FEED_TIMEOUT_MS,
    });
    if (!result.ok) {
      if (result.reason !== "error") return;
      const status = httpStatusOf(result.error);
      if (status === 404) enterCompat();
      else if (status !== undefined && status >= 500) {
        serverErrors += 1;
        if (serverErrors >= SERVER_ERRORS_BEFORE_COMPAT) enterCompat();
      }
      return;
    }
    serverErrors = 0;
    const applied = applyChanges(current, result.value);
    if (applied.needsFullReload) {
      await fetchFullQuietly();
      return;
    }
    cursor = applied.cursor;
    freshness.markSuccess();
    if (applied.list !== current) {
      queryClient.setQueryData(key, applied.list);
      options.onChanges?.();
    }
  }

  async function step(): Promise<void> {
    if (forceFull) {
      forceFull = false;
      await fetchFullQuietly();
      return;
    }
    if (mode === "compat") {
      if (now() - compatSince < COMPAT_RETRY_MS) {
        await fetchFullQuietly();
        return;
      }
      // Time to try the feed again: a full read gives the cursor to start from.
      mode = "feed";
      cursor = null;
    }
    if (cursor === null || now() - lastFullAt >= FULL_RELOAD_MS) {
      await fetchFullQuietly();
      return;
    }
    await poll();
  }

  function nextWait(): number {
    const interval =
      mode === "compat"
        ? options.isSocketUp()
          ? COMPAT_POLL_WITH_SOCKET_MS
          : COMPAT_POLL_WITHOUT_SOCKET_MS
        : FEED_INTERVAL_MS;
    return Math.max(interval, scheduler.nextDelay(FEED, interval), scheduler.nextDelay(FULL, interval));
  }

  function clearTimer() {
    if (timer !== undefined) clearT(timer);
    timer = undefined;
  }

  async function runTick(): Promise<void> {
    if (stopped) return;
    if (running) {
      pending = true;
      return;
    }
    running = true;
    try {
      await step();
    } finally {
      running = false;
    }
    if (stopped) return;
    if (pending) {
      pending = false;
      void runTick();
      return;
    }
    timer = setT(() => {
      timer = undefined;
      void runTick();
    }, nextWait());
  }

  function runNow() {
    if (stopped || !started) return;
    clearTimer();
    void runTick();
  }

  return {
    start() {
      if (started || stopped) return;
      started = true;
      void runTick();
    },
    stop() {
      stopped = true;
      clearTimer();
      scheduler.stop();
    },
    fetchFull,
    triggerPoll: runNow,
    requestFullReload() {
      // A read that just finished (or is running) already is the resync.
      if (stopped || fullInFlight || now() - lastFullAt < RELOAD_DEBOUNCE_MS) return;
      forceFull = true;
      runNow();
    },
    mode: () => mode,
  };
}
